import { useState } from "react";

import { errorMessage, useCapacity, useResizeWorkspace } from "../../api/queries";
import type { Workspace } from "../../api/types";
import {
  familyIsUnknown,
  familyLabel,
  gpuChoices,
  gpuCountLabel,
  workloadFamilyLabel,
} from "../../lib/capacity";
import { pluralize } from "../../lib/format";
import { Dialog } from "../ui/Dialog";

interface ResizeDialogProps {
  workspace: Workspace;
  onClose: () => void;
}

/**
 * Take more GPUs, or give some back — the point of the whole product.
 *
 * The counts come from the same measured capacity the request form uses, so
 * every button says what picking it means right now ("free — starts in
 * seconds" / "needs a new machine"). Giving GPUs back is always possible.
 *
 * Args:
 *     workspace: The workspace being resized.
 *     onClose: Called when the dialog is dismissed.
 *
 * Returns:
 *     The resize dialog.
 */
export function ResizeDialog({
  workspace,
  onClose,
}: ResizeDialogProps): JSX.Element {
  const [gpus, setGpus] = useState<number>(workspace.gpus);
  const capacity = useCapacity();
  const resize = useResizeWorkspace(workspace.id);

  // Resize keeps the workspace's GPU family (ADR-0020), so the counts and the
  // immediacy sentences are that family's own — always, however many families
  // the fleet happens to describe at this instant. A free machine of the
  // other GPU is not room for this one, and offering it would promise a
  // switch the server refuses.
  const family = workspace.gpu_family ?? capacity.data?.defaults.gpu_family ?? null;
  const others = (capacity.data?.families ?? [])
    .filter((entry) => entry.family !== family)
    .map((entry) => familyLabel(entry.family));
  const unreadableFamily = capacity.data
    ? familyIsUnknown(capacity.data, family)
    : false;

  const choices = capacity.data
    ? gpuChoices(capacity.data, family).map((choice) => {
        // Fewer GPUs than you already hold is always available: you hold
        // them. Only growing can depend on what is free.
        if (choice.count <= workspace.gpus) {
          return {
            ...choice,
            disabled: false,
            reason:
              choice.count === workspace.gpus
                ? "What you have now."
                : "Gives GPUs back — always possible.",
          };
        }
        return choice;
      })
    : [];

  const selected = choices.find((choice) => choice.count === gpus);
  const changed = gpus !== workspace.gpus;

  // The count this workspace holds, said the way every other screen says it.
  const heldLabel = gpuCountLabel(
    workspace.gpus,
    workloadFamilyLabel(capacity.data, workspace.gpu_family),
  );
  // One option is the count already held: an invitation to take more, with
  // nothing to take, explains nothing. Say why the list ends there instead.
  const singleSize = family !== null && !unreadableFamily && choices.length === 1;
  const held = unreadableFamily
    ? `You hold ${heldLabel}.`
    : singleSize
      ? `You hold ${heldLabel}. That is the only size these machines hold, so there is nothing else to switch to.`
      : `You hold ${heldLabel}. Take more when you need them, give some back when you do not — that is what this console is for.`;
  // The rule and the way out of it, named — a fixed choice nobody restates is
  // a fixed choice nobody can act on. Nothing at all where the fleet holds one
  // kind of GPU: there is no other one to go to.
  const wayOut =
    others.length === 1 ? `to use the ${others[0]} instead` : "to use another kind";
  const familyRule =
    family && others.length > 0
      ? (singleSize || unreadableFamily
          ? ""
          : `These are the sizes ${familyLabel(family)} machines hold. `) +
        `A workspace never changes its kind of GPU — ${wayOut}, stop this ` +
        "workspace and open a new one."
      : null;

  return (
    <Dialog
      title="Change how many GPUs you hold"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={
              resize.isPending || !changed || (selected?.disabled ?? true)
            }
            onClick={() =>
              resize.mutate({ gpus }, { onSuccess: () => onClose() })
            }
          >
            {resize.isPending
              ? "Changing…"
              : `Switch to ${pluralize(gpus, "GPU")}`}
          </button>
        </>
      }
    >
      <p className="dialog__text">{held}</p>
      {familyRule ? <p className="dialog__text">{familyRule}</p> : null}
      {capacity.isPending ? (
        <p className="dialog__text" role="status">
          Reading what is available…
        </p>
      ) : capacity.isError ? (
        <p className="dialog__error" role="alert">
          {errorMessage(capacity.error)}
        </p>
      ) : unreadableFamily && family ? (
        <p className="dialog__error" role="status">
          The console cannot say what {familyLabel(family)} machines hold right
          now. Nothing was changed — it retries by itself, try again in a
          minute.
        </p>
      ) : (
        <fieldset className="gpu-choices gpu-choices--dialog">
          <legend className="sr-only">How many GPUs</legend>
          {choices.map((choice) => (
            <label
              key={choice.count}
              className="gpu-choice"
              data-selected={gpus === choice.count}
              data-disabled={choice.disabled}
            >
              <input
                type="radio"
                name="resize-gpus"
                className="sr-only"
                value={choice.count}
                checked={gpus === choice.count}
                disabled={choice.disabled}
                aria-label={pluralize(choice.count, "GPU")}
                aria-describedby={`resize-gpu-${choice.count}`}
                onChange={() => setGpus(choice.count)}
              />
              <span className="gpu-choice__count" aria-hidden="true">
                {choice.count}
                <span className="gpu-choice__unit">
                  {choice.count === 1 ? "GPU" : "GPUs"}
                </span>
              </span>
              <span className="gpu-choice__reason" id={`resize-gpu-${choice.count}`}>
                {choice.reason}
              </span>
            </label>
          ))}
        </fieldset>
      )}
      <p className="dialog__warning" role="note">
        Changing the count restarts your environment: running processes stop,
        and anything in the fast local folder is wiped. Everything in your home
        folder is kept, and your SSH address does not change.
      </p>
      {resize.isError ? (
        <p className="dialog__error" role="alert">
          {errorMessage(resize.error)}
        </p>
      ) : null}
    </Dialog>
  );
}
